// live/foldedObservation.ts (TB4) - the pro reads the table while you're out.
//
// Joe, 2026-07-15: "lots of information from folded hands at the table." He is describing the cheapest read
// in poker: you fold, you pay nothing more, and the table keeps handing you information about who is
// capable of what. Postflop takes that away today - hero folds, the hand ends, and six players finish a pot
// you never see. TB4 gives it back.
//
// WHAT THIS FILE IS: the GH8 read layer, extended two ways.
//   1. POSTFLOP. `preflopRecap.preflopReadFor` judges a preflop action against the D1 preflop knobs
//      (`preflop.{vpipScale,limpBias,threeBetScale}`). `postflopReadFor` below does the same job on the
//      streets, against the knobs that actually govern postflop behavior: `postflop.bluffWeight`, the
//      Five-Imbalances axes (`imbalances.{bettingVolume,elasticity,polarity,equityMgmt}`), and the
//      archetype's own authored `sizingTells`.
//   2. VILLAIN-vs-VILLAIN. GH8 reads villain actions aimed at hero. Out of the hand, every action is aimed
//      at somebody else, so the read has to name who bet into whom - that is the whole shape of the
//      information ("the Rock check-raised the Firecracker" tells you about BOTH men).
//
// NO INVENTED READS (North Star §4.1). Every judgment here is a pure function of the archetype record the
// scenario engine SCRIPTED for that seat plus what the engine actually did. The engine decides what
// happened; the pro only explains it. Same contract GH8 is graded on, same $0 template lane - no API call.
//
// THE HOLE-CARD FIREWALL is upstream, in `liveRunner.buildFoldedWalk`: a `WatchedAction` has no card field,
// so nothing in this file can render a card that was not turned over. The ONLY cards here come from
// `walk.shown`, which the engine mints exclusively at a real showdown.
import type { Archetype, ArchetypeId, CardIndex, Street } from '@postflop/schema';
import type { ObserveBeat, Token } from '../observe/data';
import { type FeltView, money } from '../engine/bridge';
import { archetypeOf } from '../engine/liveData';
import { decodeCard } from '../engine/cards';
import { displayName, type FoldedWalk, type LiveHandModel, type WatchedAction } from '../engine/liveRunner';

// ── profile predicates - read STRAIGHT off the D1 archetype record, the single source of truth ──────────
// (the preflop half of this vocabulary lives in preflopRecap.ts; these are its postflop siblings.)

/** How often he fires without a hand. The shipped roster spreads across this: Rock 0.05 / Promo-chaser 0.15
 *  → 'never'; Sticker 0.3 / Sheriff 0.4 → 'rare'; Tourist 0.6 / Table captain 0.7 → 'some'; Bully 1.5 /
 *  Firecracker 1.8 → 'often'. The bands are the read. */
export type BluffBand = 'never' | 'rare' | 'some' | 'often';
export const bluffBandOf = (a: Archetype): BluffBand => {
  const w = a.postflop.bluffWeight;
  if (w <= 0.2) return 'never';
  if (w <= 0.5) return 'rare';
  if (w <= 1.0) return 'some';
  return 'often';
};

export const overCalls = (a: Archetype): boolean => a.imbalances.bettingVolume === 'overCalls';
export const overFolds = (a: Archetype): boolean => a.imbalances.bettingVolume === 'overFolds';
export const overBluffs = (a: Archetype): boolean => a.imbalances.bettingVolume === 'overBluffs';
/** the price does not move him - he calls the same with $10 or $80 in front of him. */
export const inelastic = (a: Archetype): boolean => a.imbalances.elasticity === 'inelastic';
export const overvaluesPairs = (a: Archetype): boolean => a.imbalances.equityMgmt === 'overvaluesPairs';
export const tooPolar = (a: Archetype): boolean => a.imbalances.polarity === 'tooPolar';
/** a passive station: he calls too much AND the price does not move him. The single loudest live profile -
 *  when a player like this RAISES, it is the biggest off-profile signal at the table. */
export const isStation = (a: Archetype): boolean => overCalls(a) && inelastic(a);

/** the archetype's OWN authored tell for a sizing pattern (`sizingTells` in the D1 record) - shipped data,
 *  not a guess. The same overbet is `valueHeavy/high` from the Rock and `bluffHeavy/medium` from the
 *  Firecracker; that contrast IS the "same action, different villain" lesson. */
const tellFor = (a: Archetype, pattern: 'overbet' | 'suddenRaise' | 'donkLarge' | 'minRaise') =>
  a.sizingTells?.find((t) => t.pattern === pattern);

const MEANING_PHRASE: Record<'valueHeavy' | 'bluffHeavy' | 'polarized' | 'unclear', string> = {
  valueHeavy: 'his own book says that sizing is value',
  bluffHeavy: 'his own book says that sizing leans bluff',
  polarized: 'his own book says that sizing is polarized - the nuts or nothing',
  unclear: 'his own book says that sizing means little from him',
};

const STREET_WORD: Record<Street, string> = {
  preflop: 'preflop',
  flop: 'the flop',
  turn: 'the turn',
  river: 'the river',
};

/** The read context for one WATCHED action - what postflopReadFor needs beyond the archetype. */
export interface WatchContext {
  /** he took the lead - the first aggression on this street. */
  opensStreet: boolean;
  /** he re-raised live aggression (a check-raise or a raise). */
  isReraise: boolean;
  /** there is a live bet in front of him. */
  facingBet: boolean;
  /** the player he is acting against - the villain-vs-villain axis. */
  vsName?: string;
  street: Street;
  /** his bet as a fraction of the pot before it went in; drives the authored sizing tells. */
  potFraction?: number;
}

/**
 * The pro's read of ONE villain's POSTFLOP action, watched from the rail.
 *
 * The postflop sibling of GH8's `preflopReadFor`, and it keeps that function's contract exactly: pure in
 * (archetype, action, ctx), and every read states in-profile or off-profile EXPLICITLY, because that is the
 * judgment being taught. On profile means "this is who he is - it tells you what his range is". Off profile
 * is the louder one: it means the man just did something he almost never does, and at $1/$3 that is the
 * most reliable information available.
 */
export function postflopReadFor(arch: Archetype, kind: string, ctx: WatchContext): string {
  const band = bluffBandOf(arch);
  const vs = ctx.vsName ? ` into ${ctx.vsName}` : '';
  const at = STREET_WORD[ctx.street];

  // ── a RAISE / check-raise: the loudest action in poker, and the one the profiles split hardest on ────
  if (ctx.isReraise) {
    const tell = tellFor(arch, 'suddenRaise');
    const book = tell ? ` And ${MEANING_PHRASE[tell.meaning]}.` : '';
    if (isStation(arch)) {
      return `A player who calls this much almost never raises - that is wildly off profile, and it is the single most reliable tell at a $1/$3 table. He is not getting creative${vs}: he has a hand he thinks is best and he is finally charging for it.${book}`;
    }
    if (overFolds(arch)) {
      return `He folds most hands and raises almost none, so a raise${vs} is dead on profile and about as strong a signal as ${at} produces. This is the top of his range - not a move, a monster.${book}`;
    }
    if (band === 'often') {
      return `Raising${vs} with a huge range is exactly on profile for him - it can be two pair, it can be nothing at all. The raise itself carries almost no information; what he does on the next street will say more.`;
    }
    return `He raises${vs}, which is on profile for a balanced player - mostly genuine hands with a few bluffs mixed in. Nothing exploitable here: read it as a real hand more often than a move, but not the lock a passive player's raise would be.${book}`;
  }

  // ── taking the LEAD (the first bet of the street) ────────────────────────────────────────────────────
  if (ctx.opensStreet || kind === 'bet') {
    const over = ctx.potFraction !== undefined && ctx.potFraction > 1 ? tellFor(arch, 'overbet') : undefined;
    const book = over ? ` He fired more than the pot, and ${MEANING_PHRASE[over.meaning]}.` : '';
    if (band === 'never') {
      return `He does not bluff - near zero of his bets are air. So leading ${at}${vs} is on profile only in the sense that he has it: this is value, the range behind it is narrow, and there is no bluff to catch.${book}`;
    }
    if (band === 'rare') {
      const pairy = overvaluesPairs(arch)
        ? ' And he overvalues one pair, so "a hand" can mean top pair with a bad kicker - he will bet it and pay off anyway.'
        : '';
      return `He would usually rather call than bet, so taking the lead ${at}${vs} is off his passive profile - he does this when he has decided he is good.${pairy}${book}`;
    }
    if (band === 'often') {
      const pol = tooPolar(arch)
        ? ' His betting range is too polar - huge hands and pure air, with nothing in between.'
        : '';
      return `He bets ${at}${vs} with more or less anything. Squarely on profile, so this tells you very little on its own.${pol}${book}`;
    }
    return `He takes the lead ${at}${vs}. On profile: that is inside the range he normally bets - some value, some air - so the bet alone does not narrow him much.${book}`;
  }

  if (kind === 'call') {
    if (isStation(arch)) {
      const pairy = overvaluesPairs(arch) ? ' A pair is enough for him, and it does not have to be a good one.' : '';
      return `He calls${vs}, because he calls almost everything and the price does not move him. Dead on profile and close to no information - except the useful kind: he is still in with a wide, weak range, so value-bet him and never try to bluff him off it.${pairy}`;
    }
    if (overFolds(arch)) {
      return `A call from a player who folds this much is off profile - he does not peel light. He has a real hand that was not quite worth raising.`;
    }
    if (band === 'often') {
      return `He just calls${vs} instead of raising, which caps him - his best hands come in aggressive. On profile, and it says his range here is medium, not monstrous.`;
    }
    return `He calls${vs} and keeps the pot going. On profile and nothing off it yet - a balanced flat that keeps his medium hands in. Read him on the next street.`;
  }

  if (kind === 'check') {
    if (band === 'often') {
      return `He checks${vs ? '' : ''} - and a player who bets this often checking is off profile. Either he has genuinely given up, or he is trapping. The next street will tell you which.`;
    }
    if (isStation(arch)) {
      return `He checks. On profile: he is passive, so a check is his default and means almost nothing - he will call a bet with the same range he just checked.`;
    }
    return `He checks it through. On profile and no real information - a balanced check keeps his whole range intact.`;
  }

  // ── a FOLD ───────────────────────────────────────────────────────────────────────────────────────────
  // The cheapest action in poker - but it is still HIS fold, and who folded is the read (North Star §4.1).
  // Keyed across the roster, not collapsed to a handful of canned lines: the base clause sets on/off profile
  // off his betting-volume + station profile, and a second clause keyed on a distinct axis (bluff band,
  // pair-clinginess, polarity) names what THIS man's fold specifically means. `named` (his label, minus the
  // article) puts the actual player in every sentence, so no two archetypes read the same.
  // NB: the beat that renders this already said "<Name> folds", so the read leads with the JUDGMENT, not a
  // second "he folds" - and a fold is not aimed at anyone, so no "into <name>" here.
  const named = arch.label.replace(/^The /, '');
  if (isStation(arch)) {
    const extra = overvaluesPairs(arch)
      ? ` A player who calls with any pair letting go ${at} means he flat-out missed - his continuing range just got stronger.`
      : ` He calls with the worst of it, so this fold means he genuinely had nothing.`;
    return `Now THAT is worth writing down: a ${named} folding ${at} is off profile, the loudest read a $1/$3 table gives you.${extra}`;
  }
  if (overFolds(arch)) {
    const extra =
      band === 'never'
        ? ` He never bluffs and never peels light, so the hands he does keep are the only ones that matter.`
        : ` He was never really in this pot - nothing new to learn from it.`;
    return `Dead on profile: this is how a ${named} plays, folding most of what he is dealt.${extra}`;
  }
  if (overBluffs(arch) || band === 'often') {
    return `For a ${named} this aggressive, giving it up ${at} is off profile enough to note: when he does not fire, he genuinely missed the board.`;
  }
  if (tooPolar(arch)) {
    return `On profile for a ${named}: his range here was polar, so the air just folds out and only a monster goes on.`;
  }
  if (overvaluesPairs(arch)) {
    return `On profile for a ${named}, and telling - a player who clings to any pair folding means he had literally nothing to cling to.`;
  }
  return `On profile for a ${named} and no real information - one fewer in the pot, and the read narrows to who is left.`;
}

// ── the showdown: the evidence lane ─────────────────────────────────────────────────────────────────────

/** A seat PROVEN at showdown. The three-tag read system narrows as showdowns arrive (North Star §2), and
 *  this is the arrival: the archetype the scenario engine scripted for that seat is Axis B's ground truth
 *  (§4.1), and a showdown is the moment it becomes public information. Written back to the session's
 *  persistent `SeatRead.confirmedTag` - a fact you now hold on that man for the rest of the session. */
export interface ShowdownEvidence {
  seat: number;
  /** the SCRIPTED archetype - the Axis-B ground truth, made public by the showdown. */
  archetypeId: ArchetypeId;
  cards: readonly [CardIndex, CardIndex];
  won: boolean;
}

/** "9♦ 7♦" - the shown hand as text for a beat. Only ever called on `walk.shown`. */
const cardsText = (cards: readonly [CardIndex, CardIndex]): string =>
  cards.map((c) => { const d = decodeCard(c); return `${d.rank}${d.glyph}`; }).join(' ');

const boardText = (board: readonly CardIndex[]): string =>
  board.map((c) => { const d = decodeCard(c); return `${d.rank}${d.glyph}`; }).join(' ');

/** What the showdown PROVED about a seat, given the tag you had on him. This is the confirm-or-contradict
 *  beat, and it is deliberately careful about what it claims: one showdown is evidence, not a verdict.
 *  Naming a read "confirmed" when the seat's scripted archetype matches is honest because Axis B is scored
 *  against exactly that scripted archetype (North Star §4.1) - it is the same ground truth, arriving. */
const verdictFor = (userTag: ArchetypeId | undefined, truth: ArchetypeId): 'confirmed' | 'contradicted' | 'untagged' => {
  if (!userTag) return 'untagged';
  return userTag === truth ? 'confirmed' : 'contradicted';
};

export interface FoldedObservation {
  felt: FeltView;
  beats: ObserveBeat[];
  kicker: string;
  h2: string;
  lede: string;
  /** the showdown reveal, for the card strip. Empty ⇒ no showdown; nobody's cards were ever seen. */
  shown: ShowdownEvidence[];
  /** seats to write back to the session's persistent SeatRead (same list as `shown`). */
  evidence: ShowdownEvidence[];
}

/** how the pot ended, in the pro's words. */
const outcomeWord = (walk: FoldedWalk): string =>
  walk.shown.length > 0 ? 'showdown' : 'no showdown';

/**
 * Build the folded-hand observation: what the pro watched, beat by beat, after hero folded.
 *
 * `tagsBySeat` is YOUR persistent read on each seat (`session.seats[i].read.userTags[0]`), so the showdown
 * beat can say whether what just got turned over confirms or contradicts the note you wrote on that man.
 * Pure in (model, walk, tagsBySeat) - no engine call (the walk already happened), no API, $0.
 */
export function buildFoldedObservation(
  model: LiveHandModel,
  walk: FoldedWalk,
  tagsBySeat: ReadonlyMap<number, ArchetypeId> = new Map(),
): FoldedObservation {
  const players = model.terminal.players;
  const archIdOf = (seat: number): ArchetypeId | undefined =>
    players.find((p) => p.seat === seat)?.archetypeId;
  const nameOf = (seat: number): string => {
    const id = archIdOf(seat);
    return id ? displayName(id) : `Seat ${seat}`;
  };

  const beats: ObserveBeat[] = [];

  // ── the framing beat: why watching this is worth anything ─────────────────────────────────────────
  beats.push({
    tokens: [
      `You are out. `,
      { num: money(walk.pot > 0 ? potAtFold(walk) : 0) },
      ` in the middle and ${walk.livePlayers} still in it. This is the cheapest read in poker - you have paid nothing more, and the table is about to tell you who these people are. Watch the money, not the cards.`,
    ],
  });

  // ── one beat per watched action, plus a marker when the board changes ─────────────────────────────
  let street: Street = walk.foldedOn;
  let shownBoard = 0;
  const boardAt: Record<Street, number> = { preflop: 0, flop: 3, turn: 4, river: 5 };
  shownBoard = boardAt[street];

  for (const a of walk.actions) {
    if (a.street !== street) {
      street = a.street;
      const upto = Math.min(boardAt[street], walk.board.length);
      if (upto > shownBoard) {
        const fresh = walk.board.slice(0, upto);
        beats.push({
          tokens: [
            `${street === 'flop' ? 'Flop' : street === 'turn' ? 'Turn' : 'River'} - ${boardText(fresh)}. `,
            { num: money(a.potAfter) },
            ` in the middle.`,
          ],
        });
        shownBoard = upto;
      }
    }
    const arch = archIdOf(a.seat);
    if (!arch) continue; // no archetype ⇒ nothing to read him against; never invent one.
    beats.push(actionBeat(a, arch, nameOf));
  }

  // ── the showdown: the evidence lane ───────────────────────────────────────────────────────────────
  const shown: ShowdownEvidence[] = walk.shown.flatMap((s) => {
    const id = archIdOf(s.seat);
    return id ? [{ seat: s.seat, archetypeId: id, cards: s.cards, won: s.won }] : [];
  });

  if (shown.length === 0) {
    const winner = walk.winners[0];
    const winnerArch = winner !== undefined ? archIdOf(winner) : undefined;
    // CX9: if the winner is a known type, make his name tap-to-learn; otherwise it stays plain prose.
    const lead: Token[] =
      winner !== undefined
        ? winnerArch
          ? [{ archetype: winnerArch, text: nameOf(winner) }, ` takes it down without a showdown - `]
          : [`${nameOf(winner)} takes it down without a showdown - `]
        : [`The pot is pushed without a showdown - `];
    beats.push({
      decision: true,
      tag: 'No showdown',
      tokens: [
        ...lead,
        { num: money(walk.pot) },
        `. Nobody's cards ever hit the felt, so nobody learned anything about anybody. That is the honest result: you only get a read when someone pays to see it.`,
      ],
    });
  } else {
    for (const s of shown) {
      const arch = archetypeOf(s.archetypeId);
      const verdict = verdictFor(tagsBySeat.get(s.seat), s.archetypeId);
      beats.push({
        decision: true,
        tag: verdict === 'confirmed' ? 'Read confirmed' : verdict === 'contradicted' ? 'Read contradicted' : 'Free read',
        tokens: [
          // CX9: tap the type he turned out to be to learn what it is + how to adjust.
          { archetype: s.archetypeId, text: nameOf(s.seat) },
          ` shows ${cardsText(s.cards)} and ${s.won ? 'takes' : 'loses'} `,
          { num: money(walk.pot) },
          `. `,
          showdownRead(arch, verdict, tagsBySeat.get(s.seat)),
        ],
      });
    }
  }

  return {
    felt: observationFelt(model, walk),
    beats,
    kicker: 'While you were out',
    h2: 'The pro reads the table',
    lede: `You folded and the hand played on - ${outcomeWord(walk)}. Every chip that moved without you is free information about the men you still have to play.`,
    shown,
    evidence: shown,
  };
}

/** the pot as it stood the moment hero folded - the first watched action's pot, less what that action put
 *  in, is fragile; the honest simple answer is the pot before the first watched action. */
const potAtFold = (walk: FoldedWalk): number => {
  const first = walk.actions[0];
  if (!first) return walk.pot;
  // the first watched action's potAfter minus what that action itself contributed.
  return first.potAfter - (first.kind === 'fold' || first.kind === 'check' ? 0 : first.amount);
};

/** one villain action, narrated + read. */
const actionBeat = (
  a: WatchedAction,
  archId: ArchetypeId,
  nameOf: (seat: number) => string,
): ObserveBeat => {
  const arch = archetypeOf(archId);
  const vsName = a.vsSeat !== undefined ? nameOf(a.vsSeat) : undefined;
  const potBefore = a.potAfter - (a.kind === 'fold' || a.kind === 'check' ? 0 : a.amount);
  const ctx: WatchContext = {
    opensStreet: a.opensStreet,
    isReraise: a.isReraise,
    facingBet: a.vsSeat !== undefined,
    ...(vsName ? { vsName } : {}),
    street: a.street,
    ...(potBefore > 0 && a.amount > 0 ? { potFraction: a.amount / potBefore } : {}),
  };
  const read = postflopReadFor(arch, a.kind, ctx);
  return {
    // CX9: the acting player's TYPE name is tap-to-learn; the rest of the line stays prose.
    tokens: [
      { archetype: archId, text: nameOf(a.seat) },
      ` ${watchedPhrase(a, vsName)}. `,
      read,
      ` Pot `,
      { num: money(a.potAfter) },
      `.`,
    ],
  };
};

/** the plain-English phrase for a watched action - third person, and it NAMES THE TARGET, because out of
 *  the hand the whole point is who is doing what to whom. */
const watchedPhrase = (a: WatchedAction, vsName?: string): string => {
  const into = vsName ? ` into ${vsName}` : '';
  const over = vsName ? ` over ${vsName}` : '';
  if (a.kind === 'bet') return `bets ${money(a.amount)}${into}`;
  if (a.kind === 'raise') return `raises to ${money(a.amount)}${over}`;
  if (a.kind === 'jam') return `jams all in${over}`;
  if (a.kind === 'call') return vsName ? `calls ${money(a.amount)}` : `calls ${money(a.amount)}`;
  if (a.kind === 'check') return `checks`;
  return `folds`;
};

/** the read on a hand that just got turned over. Careful about what it claims: a showdown is evidence. */
const showdownRead = (arch: Archetype, verdict: 'confirmed' | 'contradicted' | 'untagged', userTag?: ArchetypeId): string => {
  const who = arch.label;
  if (verdict === 'confirmed') {
    return `That is exactly the range you tagged him for - the read holds, and it is now a fact you own on this seat for the rest of the session. This is what a folded hand buys you.`;
  }
  if (verdict === 'contradicted') {
    return `That is NOT the player you tagged him as. You had him as ${userTag ? archetypeOf(userTag).label : 'someone else'}; he just showed you he is ${who}. Re-tag him - a read you refuse to update is worse than no read at all.`;
  }
  return `You had no read on him yet. Now you do: he is ${who}, and you got it for free, out of a pot you were not even in.`;
};

/** the aftermath felt: the real final board, the real final pot, the real final stacks, hero folded. Built
 *  off the hand's own last felt so the ring, the names, the button, and the persisted tags are the same
 *  ones you were just looking at - this is the SAME table, one moment later. */
const observationFelt = (model: LiveHandModel, walk: FoldedWalk): FeltView => {
  const base = model.steps[model.steps.length - 1]?.felt ?? model.preflop?.felt ?? model.snapshots[0]?.felt;
  const stackOf = new Map(walk.seats.map((s) => [s.seat, s]));
  if (!base) {
    return {
      seats: [],
      board: [...walk.board],
      pot: money(walk.pot),
      heroCards: model.heroCards,
      heroLabel: 'You - folded',
      heroHasButton: model.terminal.buttonSeat === model.heroSeat,
    };
  }
  return {
    ...base,
    board: [...walk.board],
    pot: money(walk.pot),
    heroLabel: 'You - folded',
    seats: base.seats.map((s) => {
      const end = s.seat !== undefined ? stackOf.get(s.seat) : undefined;
      return {
        ...s,
        ...(end ? { stack: money(end.stack), out: end.folded } : {}),
        // the aftermath is a still frame: no live aggressor, no street bet chips.
        active: false,
        bet: '',
      };
    }),
  };
};
